

import { useEffect, useRef, useState } from 'react';
import { Stack, Button, Group, Box, Alert, Text, Loader } from '@mantine/core';
import { IconAlertCircle, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { shareLinksApi } from '@/services/share-links.api';
import { sessionsApi } from '@/services/sessions.api';
import { FlowDSL } from '@holloway/dsl';
import SimulatorCurrentNode from './Simulator/SimulatorCurrentNode';
import SimulatorStartCard from './Simulator/SimulatorStartCard';

interface Props {
  flowId?: string;
  slug?: string;
  getCurrentFlowDSL?: () => FlowDSL;
  flowDSL?: FlowDSL;
  mode?: 'preview' | 'live';
  /**
   * Share-link token. When present the run is started from the link rather than
   * a published slug — the token is the only credential the caller has.
   */
  shareToken?: string;
  onClose?: () => void;
  isFullscreen?: boolean;
  /** Skip the start card and open the session immediately (and again after a reset). */
  autoStart?: boolean;
}

export default function SimulatorPanel({
  flowId,
  slug,
  getCurrentFlowDSL,
  flowDSL,
  mode = 'preview',
  shareToken,
  onClose,
  isFullscreen = false,
  autoStart = false
}: Props) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState<Record<string, any>>({});
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [sessionState, setSessionState] = useState<any>({});
  const [completed, setCompleted] = useState(false);
  const [isRewinding, setIsRewinding] = useState(false);
  const [backError, setBackError] = useState<unknown>(null);
  // The flow as it was when the preview started. The server keeps nothing
  // between preview steps, so every step sends it back; freezing it here keeps
  // edits made mid-run from changing the flow under a run already in progress.
  const previewFlowRef = useRef<FlowDSL | null>(null);
  const [history, setHistory] = useState<Array<{
    breadcrumbs: string[];
    currentNode: any;
    input: Record<string, any>;
    sessionState: any;
    completed: boolean;
  }>>([]);

  const createSession = useMutation({
    mutationFn: async () => {
      if (mode === 'live') {
        // A share-link run is a real session like any other; it is just
        // authorized by the token instead of by a published slug.
        if (shareToken) {
          return await shareLinksApi.createSession(shareToken);
        }
        if (!slug) throw new Error('Slug is required for live mode');
        const result = await sessionsApi.createSession(slug);
        return result;
      } else {
        let flow = flowDSL;
        if (getCurrentFlowDSL) {
          flow = getCurrentFlowDSL();
        }

        if (!flow) throw new Error('Flow DSL is required for preview mode');

        if (!flow.nodes.find((n: any) => n.type === 'Start')) {
          throw new Error('Flow must have a Start node to run preview. Please add a Start node.');
        }

        const result = await sessionsApi.createPreviewSession(flow);
        previewFlowRef.current = flow;
        return result;
      }
    },
    onSuccess: (data) => {
      setSessionId(data.session.id);
      setBreadcrumbs(data.session.breadcrumbs || []);
      setCurrentNode(data.currentNode);
      setSessionState(data.session.state || {});
      setCompleted(data.session.completed || data.currentNode?.type === 'End');
      setInput({});
      setHistory([]);
    },
  });

  // Opens the session on mount so the viewer lands on the first page. The ref keeps
  // StrictMode's double-mount (and a failed attempt) from creating a second session,
  // and re-arms once a session exists so Start Over auto-starts the next one too.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart) return;
    if (sessionId) {
      autoStartedRef.current = false;
      return;
    }
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    createSession.mutate();
  }, [autoStart, sessionId]);

  const advance = useMutation({
    mutationFn: (branchTarget?: string) => {
      const advanceInput = branchTarget 
        ? { ...input, _branchTarget: branchTarget }
        : input;
      
      if (mode === 'live') {
        return sessionsApi.advanceSession(sessionId!, advanceInput);
      } else {
        return sessionsApi.stepPreview(
          previewFlowRef.current!,
          {
            sessionId: sessionId!,
            state: sessionState,
            currentNodeId: currentNode.id,
            breadcrumbs,
            completed,
          },
          advanceInput,
        );
      }
    },
    onSuccess: (data) => {
      setBreadcrumbs(data.session.breadcrumbs || []);
      setCurrentNode(data.currentNode);
      setSessionState(data.session.state || {});
      setCompleted(data.session.completed || data.currentNode?.type === 'End');
      setInput({});
    },
  });

  const handleAdvance = (branchTarget?: string) => {
    if (!sessionId || !currentNode || advance.isPending) return;
    setBackError(null);

    setHistory((entries) => [
      ...entries,
      {
        breadcrumbs,
        currentNode,
        input,
        sessionState,
        completed,
      },
    ]);
    advance.mutate(branchTarget);
  };

  const handleBack = () => {
    const previous = history.at(-1);
    if (!previous || isRewinding) return;

    const applyPrevious = () => {
      setBreadcrumbs(previous.breadcrumbs);
      setCurrentNode(previous.currentNode);
      setInput(previous.input);
      setSessionState(previous.sessionState);
      setCompleted(previous.completed);
      setHistory((entries) => entries.slice(0, -1));
    };

    // Preview's server keeps no run to step back, so the snapshot is the whole
    // story.
    if (!sessionId || mode !== 'live') {
      applyPrevious();
      return;
    }

    // A live session is persisted server-side and must be stepped back there too.
    // Rewinding only the browser left the server on the later node, so the next
    // advance was processed as that node and the corrected answer was discarded.
    // No client-only fallback on failure, for the same reason: staying put with an
    // error is better than showing an earlier page the server is not on.
    setIsRewinding(true);
    setBackError(null);
    sessionsApi
      .backSession(sessionId, currentNode?.id)
      .then((data) => {
        setBreadcrumbs(data.session.breadcrumbs || []);
        setCurrentNode(data.currentNode);
        setInput(previous.input);
        setSessionState(data.session.state || {});
        setCompleted(data.session.completed || data.currentNode?.type === 'End');
        setHistory((entries) => entries.slice(0, -1));
      })
      .catch((error) => {
        setBackError(error);
      })
      .finally(() => {
        setIsRewinding(false);
      });
  };

  const handleReset = () => {
    setSessionId(null);
    previewFlowRef.current = null;
    setInput({});
    setBreadcrumbs([]);
    setCurrentNode(null);
    setSessionState({});
    setCompleted(false);
    setHistory([]);
    setBackError(null);
  };


  return (
    <Box
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F2F4F7',
        borderRadius: isFullscreen ? 0 : 8,
        overflow: 'hidden',
      }}
    >
      {/* Exit Preview Bar */}
      {onClose && (
        <Group justify="flex-end" px="lg" py="sm">
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconX size={16} />}
            onClick={onClose}
            styles={{ root: { fontWeight: 500 } }}
          >
            Exit Preview
          </Button>
        </Group>
      )}

      {/* Main Content Area */}
      <Box
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 24px',
        }}
      >
        {/* Error State */}
        {(createSession.isError || advance.isError || backError != null) && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="md" w="100%" maw={640}>
            {createSession.isError && (
              <div>
                <strong>Failed to create session:</strong>
                <pre style={{ fontSize: '11px', marginTop: '8px', overflow: 'auto' }}>
                  {JSON.stringify((createSession.error as any)?.response?.data || (createSession.error as any)?.message || createSession.error, null, 2)}
                </pre>
              </div>
            )}
            {advance.isError && (
              <div>
                <strong>Failed to advance:</strong>
                <pre style={{ fontSize: '11px', marginTop: '8px', overflow: 'auto' }}>
                  {JSON.stringify((advance.error as any)?.response?.data || (advance.error as any)?.message || advance.error, null, 2)}
                </pre>
              </div>
            )}
            {backError != null && (
              <div>
                <strong>Failed to go back:</strong>
                <pre style={{ fontSize: '11px', marginTop: '8px', overflow: 'auto' }}>
                  {JSON.stringify((backError as any)?.response?.data || (backError as any)?.message || backError, null, 2)}
                </pre>
              </div>
            )}
          </Alert>
        )}

        {!sessionId ? (
          // Auto-start shows a loader instead of the start card, but still falls back to
          // the card if the session failed, so the viewer has a way to retry.
          autoStart && !createSession.isError ? (
            <Loader mt="xl" />
          ) : (
            <SimulatorStartCard onStart={() => createSession.mutate()} isLoading={createSession.isPending} />
          )
        ) : (
          <Stack gap="md" w="100%" maw={640} align="center">
            {/* Current Node */}
            <SimulatorCurrentNode
              currentNode={currentNode}
              input={input}
              onInputChange={setInput}
              onAdvance={handleAdvance}
              onBack={history.length > 0 ? handleBack : undefined}
              isAdvancing={advance.isPending || isRewinding}
              completed={completed}
              onReset={handleReset}
              sessionState={sessionState?.vars || {}}
              sessionId={sessionId}
              mode={mode}
            />
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Box py="md" style={{ textAlign: 'center' }}>
        <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.08em' }}>
          Powered by Holloway • Secured with TLS 1.3
        </Text>
      </Box>
    </Box>
  );
}

